import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { GeneriqueData, Departement, SysTypeDocument } from '../models/models';
import { ScopeType, ScopeEffect } from '../constants/enums';
import api from './api';

/** Map d'override : scope_id → effect (BLOCK = force bloqué, ALLOW = ré-autorise) */
export type UserScopeMap = Record<number, ScopeEffect>;

/** État 3-positions pour RESTRICT_TO_CREATOR :
 *   INHERIT = hérite du rôle
 *   BLOCK   = restriction forcée active (override OU additif)
 *   ALLOW   = restriction forcée désactivée (override du rôle)
 */
export type RestrictOverride = 'INHERIT' | 'BLOCK' | 'ALLOW';

interface ScopeState {
  // ── Scopes des rôles (blacklist simple — pas d'override côté rôle) ──────
  roleScopesDept:              number[];
  roleScopesDepot:             number[];
  roleScopesCaisse:            number[];
  roleScopesTypeDoc:           number[];
  roleScopesRestrictToCreator: boolean;
  // ── Overrides direct du user (peuvent être BLOCK ou ALLOW) ──────────────
  userScopesDept:    UserScopeMap;
  userScopesDepot:   UserScopeMap;
  userScopesCaisse:  UserScopeMap;
  userScopesTypeDoc: UserScopeMap;
  userScopesRestrictToCreator: RestrictOverride;
  // ── Éléments autorisés pour l'utilisateur connecté (selects) ────────────
  allowedDepots:           GeneriqueData[];
  allowedCaisses:          GeneriqueData[];
  allowedDepartements:     Departement[];
  allowedSysTypeDocuments: SysTypeDocument[];
  loading:        boolean;
  error:          string | null;
  successMessage: string | null;
}

const initialState: ScopeState = {
  roleScopesDept:              [],
  roleScopesDepot:             [],
  roleScopesCaisse:            [],
  roleScopesTypeDoc:           [],
  roleScopesRestrictToCreator: false,
  userScopesDept:              {},
  userScopesDepot:             {},
  userScopesCaisse:            {},
  userScopesTypeDoc:           {},
  userScopesRestrictToCreator: 'INHERIT',
  allowedDepots:           [],
  allowedCaisses:          [],
  allowedDepartements:     [],
  allowedSysTypeDocuments: [],
  loading:        false,
  error:          null,
  successMessage: null,
};

// ── Éléments autorisés pour l'utilisateur connecté ───────────────────────────

export const fetchAllowedDepots = createAsyncThunk<
  GeneriqueData[],
  { departement_id?: number | null },
  { rejectValue: string }
>('scope/fetchAllowedDepots', async ({ departement_id }, thunkAPI) => {
  try {
    const response = await api.get('/scopes/allowed/depot', { params: { departement_id } });
    return response.data as GeneriqueData[];
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.response?.data?.message || 'Erreur chargement dépôts autorisés.');
  }
});

export const fetchAllowedCaisses = createAsyncThunk<
  GeneriqueData[],
  { departement_id?: number | null },
  { rejectValue: string }
>('scope/fetchAllowedCaisses', async ({ departement_id }, thunkAPI) => {
  try {
    const response = await api.get('/scopes/allowed/caisse', { params: { departement_id } });
    return response.data as GeneriqueData[];
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.response?.data?.message || 'Erreur chargement caisses autorisées.');
  }
});

export const fetchAllowedDepartements = createAsyncThunk<
  Departement[],
  void,
  { rejectValue: string }
>('scope/fetchAllowedDepartements', async (_, thunkAPI) => {
  try {
    const response = await api.get('/scopes/allowed/departement');
    return response.data as Departement[];
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.response?.data?.message || 'Erreur chargement départements autorisés.');
  }
});

export const fetchAllowedSysTypeDocuments = createAsyncThunk<
  SysTypeDocument[],
  void,
  { rejectValue: string }
>('scope/fetchAllowedSysTypeDocuments', async (_, thunkAPI) => {
  try {
    const response = await api.get('/scopes/allowed/typeDocument');
    return response.data as SysTypeDocument[];
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.response?.data?.message || 'Erreur chargement types de document autorisés.');
  }
});

// ── Scopes de rôle (blacklist simple) ────────────────────────────────────────

export const fetchRoleScopes = createAsyncThunk<
  { scope_type: ScopeType; data: number[] },
  { role_id: number; scope_type: ScopeType },
  { rejectValue: string }
>('scope/fetchRole', async ({ role_id, scope_type }, thunkAPI) => {
  try {
    const response = await api.get(`/scopes/role/${role_id}`, { params: { scope_type } });
    return { scope_type, data: response.data.data as number[] };
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.response?.data?.message || 'Erreur chargement scopes rôle.');
  }
});

export const setRoleScopes = createAsyncThunk<
  void,
  { role_id: number; scope_type: ScopeType; scope_ids: number[] },
  { rejectValue: string }
>('scope/setRole', async ({ role_id, scope_type, scope_ids }, thunkAPI) => {
  try {
    await api.post(`/scopes/role/${role_id}`, { scope_type, scope_ids });
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.response?.data?.message || 'Erreur mise à jour scopes rôle.');
  }
});

// ── Scopes directs d'un user (avec override BLOCK/ALLOW) ─────────────────────

interface UserScopeRow { scope_id: number; effect: ScopeEffect; }

export const fetchUserScopes = createAsyncThunk<
  { scope_type: ScopeType; data: UserScopeRow[] },
  { user_id: number; scope_type: ScopeType },
  { rejectValue: string }
>('scope/fetchUser', async ({ user_id, scope_type }, thunkAPI) => {
  try {
    const response = await api.get(`/scopes/user/${user_id}`, { params: { scope_type } });
    return { scope_type, data: response.data.data as UserScopeRow[] };
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.response?.data?.message || 'Erreur chargement scopes user.');
  }
});

export const setUserScopes = createAsyncThunk<
  void,
  { user_id: number; scope_type: ScopeType; blocks: number[]; allows: number[] },
  { rejectValue: string }
>('scope/setUser', async ({ user_id, scope_type, blocks, allows }, thunkAPI) => {
  try {
    await api.post(`/scopes/user/${user_id}`, { scope_type, blocks, allows });
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.response?.data?.message || 'Erreur mise à jour scopes user.');
  }
});

// ── Helper : convertit la liste backend en map effect ────────────────────────

const toMap = (rows: UserScopeRow[]): UserScopeMap =>
  rows.reduce<UserScopeMap>((acc, r) => { acc[r.scope_id] = r.effect; return acc; }, {});

// ── Slice ────────────────────────────────────────────────────────────────────

const scopeSlice = createSlice({
  name: 'ScopeSlice',
  initialState,
  reducers: {
    clearScopeData(state) {
      state.roleScopesDept              = [];
      state.roleScopesDepot             = [];
      state.roleScopesCaisse            = [];
      state.roleScopesTypeDoc           = [];
      state.roleScopesRestrictToCreator = false;
      state.userScopesDept              = {};
      state.userScopesDepot             = {};
      state.userScopesCaisse            = {};
      state.userScopesTypeDoc           = {};
      state.userScopesRestrictToCreator = 'INHERIT';
    },
    clearAllowedScopes(state) {
      state.allowedDepots           = [];
      state.allowedCaisses          = [];
      state.allowedDepartements     = [];
      state.allowedSysTypeDocuments = [];
    },
    clearScopeMessages(state) {
      state.error          = null;
      state.successMessage = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchRoleScopes
      .addCase(fetchRoleScopes.pending,   (state) => { state.loading = true; state.error = null; })
      .addCase(fetchRoleScopes.fulfilled, (state, action) => {
        state.loading = false;
        const { scope_type, data } = action.payload;
        if (scope_type === ScopeType.DEPARTEMENT)   state.roleScopesDept    = data;
        if (scope_type === ScopeType.DEPOT)         state.roleScopesDepot   = data;
        if (scope_type === ScopeType.CAISSE)        state.roleScopesCaisse  = data;
        if (scope_type === ScopeType.TYPE_DOCUMENT) state.roleScopesTypeDoc = data;
        if (scope_type === ScopeType.RESTRICT_TO_CREATOR) state.roleScopesRestrictToCreator = data.length > 0;
      })
      .addCase(fetchRoleScopes.rejected,  (state, action) => { state.loading = false; state.error = action.payload || 'Erreur'; })

      // setRoleScopes
      .addCase(setRoleScopes.fulfilled, (state) => { state.successMessage = 'Scopes du rôle mis à jour.'; })
      .addCase(setRoleScopes.rejected,  (state, action) => { state.error = action.payload || 'Erreur'; })

      // fetchUserScopes
      .addCase(fetchUserScopes.pending,   (state) => { state.loading = true; state.error = null; })
      .addCase(fetchUserScopes.fulfilled, (state, action) => {
        state.loading = false;
        const { scope_type, data } = action.payload;
        if (scope_type === ScopeType.DEPARTEMENT)   state.userScopesDept    = toMap(data);
        if (scope_type === ScopeType.DEPOT)         state.userScopesDepot   = toMap(data);
        if (scope_type === ScopeType.CAISSE)        state.userScopesCaisse  = toMap(data);
        if (scope_type === ScopeType.TYPE_DOCUMENT) state.userScopesTypeDoc = toMap(data);
        // RESTRICT_TO_CREATOR : 3 états selon présence + effect (placeholder scope_id=0)
        if (scope_type === ScopeType.RESTRICT_TO_CREATOR) {
          if (data.length === 0) state.userScopesRestrictToCreator = 'INHERIT';
          else                   state.userScopesRestrictToCreator = data[0].effect;
        }
      })
      .addCase(fetchUserScopes.rejected,  (state, action) => { state.loading = false; state.error = action.payload || 'Erreur'; })

      // setUserScopes
      .addCase(setUserScopes.fulfilled, (state) => { state.successMessage = 'Scopes utilisateur mis à jour.'; })
      .addCase(setUserScopes.rejected,  (state, action) => { state.error = action.payload || 'Erreur'; })

      // fetchAllowedDepots
      .addCase(fetchAllowedDepots.fulfilled, (state, action) => { state.allowedDepots = action.payload; })

      // fetchAllowedCaisses
      .addCase(fetchAllowedCaisses.fulfilled, (state, action) => { state.allowedCaisses = action.payload; })

      // fetchAllowedDepartements
      .addCase(fetchAllowedDepartements.fulfilled, (state, action) => { state.allowedDepartements = action.payload; })

      // fetchAllowedSysTypeDocuments
      .addCase(fetchAllowedSysTypeDocuments.fulfilled, (state, action) => { state.allowedSysTypeDocuments = action.payload; });
  },
});

export const { clearScopeData, clearScopeMessages, clearAllowedScopes } = scopeSlice.actions;
export default scopeSlice.reducer;
